import React, { useMemo } from "react";
import { DataTable, PALETTE, Stars, formatMinutes, formatPercent } from "./Primitives";

const MEDALS = ["🥇", "🥈", "🥉"];
const MEDAL_COLORS = [PALETTE.green, PALETTE.blue, PALETTE.amber];

const closureColor = (minutes) => {
    if (minutes > 45) return PALETTE.red;
    if (minutes > 30) return PALETTE.amber;
    return PALETTE.green;
};

const escalationStyle = (count) => {
    if (count >= 4) return { background: "#FFF0F0", color: PALETTE.accent };
    if (count >= 2) return { background: "#FFFBEB", color: PALETTE.amber };
    return { background: "#F0FDF4", color: PALETTE.green };
};

export default function StaffTab({ staff }) {
    const rows = useMemo(() => staff?.staff ?? [], [staff]);
    const active = useMemo(() => rows.filter((row) => row.assigned > 0), [rows]);
    const top = useMemo(() => [...active].sort((a, b) => b.completed - a.completed).slice(0, 3), [active]);

    const columns = [
        {
            key: "name",
            label: "Employee",
            width: "1.8fr",
            render: (row) => (
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <div
                        style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            background: PALETTE.red,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            color: "#fff",
                            fontWeight: 700,
                            flex: "0 0 auto",
                        }}
                    >
                        {(row.name || "?")
                            .split(" ")
                            .map((word) => word[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1a1a2e" }}>{row.name}</div>
                        {row.department && (
                            <div style={{ fontSize: 11, color: "#9097ac" }}>{row.department}</div>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: "assigned",
            label: "Assigned",
            render: (row) => (
                <span style={{ fontSize: 13.5, color: "#69708A" }}>{row.assigned}</span>
            ),
        },
        {
            key: "completed",
            label: "Completed",
            render: (row) => (
                <span style={{ fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>{row.completed}</span>
            ),
        },
        {
            key: "completion_rate",
            label: "Completion",
            render: (row) =>
                row.assigned > 0 ? (
                    <span
                        style={{
                            fontSize: 13.5,
                            fontWeight: 700,
                            color:
                                row.completion_rate >= 80
                                    ? PALETTE.green
                                    : row.completion_rate >= 50
                                      ? PALETTE.amber
                                      : PALETTE.red,
                        }}
                    >
                        {formatPercent(row.completion_rate)}
                    </span>
                ) : (
                    <span style={{ fontSize: 12, color: "#9097ac" }}>—</span>
                ),
        },
        {
            key: "avg_closure_minutes",
            label: "Avg Closure",
            render: (row) => (
                <span style={{ fontSize: 13.5, fontWeight: 600, color: closureColor(row.avg_closure_minutes) }}>
                    {row.avg_closure_minutes > 0 ? formatMinutes(row.avg_closure_minutes) : "—"}
                </span>
            ),
        },
        {
            key: "escalations",
            label: "Escalations",
            render: (row) => (
                <span
                    style={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        padding: "3px 11px",
                        borderRadius: 20,
                        ...escalationStyle(row.escalations),
                    }}
                >
                    {row.escalations}
                </span>
            ),
        },
        {
            key: "rating",
            label: "Rating",
            render: (row) =>
                row.rating_count > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <Stars rating={row.avg_rating} />
                        <span style={{ fontSize: 10.5, color: "#9097ac" }}>
                            {row.avg_rating.toFixed(1)} · {row.rating_count}
                        </span>
                    </div>
                ) : (
                    <span style={{ fontSize: 12, color: "#9097ac" }}>—</span>
                ),
        },
    ];

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
                {top.map((member, index) => (
                    <div
                        key={member.staff_member_id}
                        style={{
                            background: "#fff",
                            borderRadius: 12,
                            padding: "14px 16px",
                            border: "1.5px solid #ECEEF5",
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                        }}
                    >
                        <span style={{ fontSize: 22 }}>{MEDALS[index]}</span>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#9097ac", letterSpacing: 0.5 }}>
                                {`TOP PERFORMER #${index + 1}`}
                            </div>
                            <div style={{ fontSize: 14.5, fontWeight: 700, color: MEDAL_COLORS[index], marginTop: 2 }}>
                                {member.name}
                            </div>
                            <div style={{ fontSize: 11, color: "#9097ac" }}>
                                {member.completed} completed
                                {member.avg_closure_minutes > 0
                                    ? ` · ${formatMinutes(member.avg_closure_minutes)} avg closure`
                                    : ""}
                            </div>
                        </div>
                    </div>
                ))}
                {top.length === 0 && (
                    <div
                        style={{
                            gridColumn: "1 / -1",
                            background: "#fff",
                            borderRadius: 12,
                            padding: "18px 20px",
                            border: "1.5px dashed #D8DCE8",
                            fontSize: 12.5,
                            color: "#9097ac",
                            textAlign: "center",
                        }}
                    >
                        No requests were assigned to staff in this period
                    </div>
                )}
            </div>

            {staff?.unassigned_requests > 0 && (
                <div
                    style={{
                        marginBottom: 16,
                        background: "#FFFBEB",
                        border: "1px solid #fde68a",
                        borderRadius: 12,
                        padding: "12px 16px",
                        fontSize: 12.5,
                        color: "#92400e",
                    }}
                >
                    <b>{staff.unassigned_requests}</b> request
                    {staff.unassigned_requests === 1 ? " was" : "s were"} never assigned to a staff member — these
                    are excluded from the per-employee figures below.
                </div>
            )}

            <DataTable
                columns={columns}
                rows={rows.map((row) => ({ ...row, key: row.staff_member_id }))}
                emptyMessage="No staff members configured for this property."
            />
        </div>
    );
}
